import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  rootId,
  TreeItem,
  TreeItemTypes,
} from 'shared/components/ui/virtualized-tree-list';
import { ID } from 'shared/types/id';
import { LinkedItemSection } from 'shared/types/linked-item-section';
import { SortingMode } from 'shared/types/sorting-mode';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { getSortedTasks } from 'shared/utils/get-sorted-tasks';
import { noGroupingId } from 'shared/views/task-filter-list/types';

import { filterEmptyChildrenCascading } from './use-life-area-tree';

export type Options = {
  tasks: Task[];
  sections?: LinkedItemSection[] | null;
  customOrder?: Record<ID, ID[]>;
  sortingMode?: SortingMode | null;
  today: Timestamp;
  weekStartsOn: WeekDays;
  showEmptyGroups?: boolean;
};

export const useSectionTree = ({
  tasks,
  sections = [],
  customOrder,
  sortingMode,
  today,
  weekStartsOn,
  showEmptyGroups,
}: Options) => {
  const { t } = useTranslation();

  return useMemo(() => {
    const _sections = sections ?? [];
    // Create section tree items
    const sectionTreeItems = _sections.reduce<Record<ID, TreeItem>>(
      (acc, section) => {
        acc[section.id] = {
          id: section.id,
          type: TreeItemTypes.Section,
          name: section.name,
          children: [],
          isFolder: true,
        };
        return acc;
      },
      {},
    );

    // Add "No Section" group only if we have other sections
    if (_sections.length) {
      sectionTreeItems[noGroupingId] = {
        id: noGroupingId,
        type: TreeItemTypes.Section,
        name: t('general.labels.goal.sections.other.label'),
        children: [],
        isFolder: true,
      };
    }

    const sectionIds = Object.keys(sectionTreeItems);

    // Build custom task order
    const customTaskOrder =
      sortingMode === SortingMode.Custom
        ? _sections.length
          ? sectionIds.reduce<ID[]>((acc, sectionId) => {
              const order = customOrder?.[sectionId] ?? [];
              acc.push(...order);
              return acc;
            }, [])
          : customOrder?.[noGroupingId] ?? []
        : [];

    const sortedTasks = getSortedTasks(tasks, {
      today,
      weekStartsOn,
      order: customTaskOrder,
      mode: sortingMode ? [SortingMode.DueDate, sortingMode] : undefined,
    });

    if (!_sections.length) {
      const taskItems: Record<ID, TreeItem> = {};
      sortedTasks.forEach((task) => {
        taskItems[task.id] = {
          id: task.id,
          name: task.name,
          type: TreeItemTypes.Task,
        };
      });

      const newTaskItemId = `${noGroupingId}-new-task`;
      taskItems[newTaskItemId] = {
        id: newTaskItemId,
        name: 'New Task',
        type: TreeItemTypes.NewTask,
      };

      return {
        [rootId]: {
          id: rootId,
          name: 'Root',
          type: TreeItemTypes.Root,
          children: [...sortedTasks.map((t) => t.id), newTaskItemId],
        },
        ...taskItems,
      };
    }

    const tasksInSections = new Set<ID>();

    // Add tasks to their sections
    _sections.forEach((section) => {
      const sectionTaskIds = new Set(section.tasks);

      const tasksForThisSection = sortedTasks.filter((t) =>
        sectionTaskIds.has(t.id),
      );

      tasksForThisSection.forEach((task) => {
        sectionTreeItems[section.id].children?.push(task.id);
        tasksInSections.add(task.id);
      });
    });

    // Handle No Section
    const noSectionTasks = sortedTasks.filter(
      (t) => !tasksInSections.has(t.id),
    );
    noSectionTasks.forEach((task) => {
      sectionTreeItems[noGroupingId].children?.push(task.id);
    });

    // Create lookup for all items
    const allTreeItems: Record<ID, TreeItem> = { ...sectionTreeItems };
    sortedTasks.forEach((task) => {
      allTreeItems[task.id] = {
        id: task.id,
        name: task.name,
        type: TreeItemTypes.Task,
      };
    });

    // Add New Task row
    Object.keys(sectionTreeItems).forEach((sectionId) => {
      const sectionItem = allTreeItems[sectionId];
      if (sectionItem) {
        if (!sectionItem.children?.length && !showEmptyGroups) {
          return;
        }

        const newTaskItemId = `${sectionId}-new-task`;
        sectionItem.children?.push(newTaskItemId);
        allTreeItems[newTaskItemId] = {
          id: newTaskItemId,
          name: 'New Task',
          type: TreeItemTypes.NewTask,
        };
      }
    });

    // Filter empty
    const filteredTreeItems = filterEmptyChildrenCascading(allTreeItems);
    const topLevelIds = Object.keys(filteredTreeItems).filter(
      (id) => filteredTreeItems[id].type === TreeItemTypes.Section,
    );

    return {
      [rootId]: {
        id: rootId,
        name: 'Root',
        type: TreeItemTypes.Root,
        children: topLevelIds,
      },
      ...filteredTreeItems,
    };
  }, [
    sections,
    customOrder,
    sortingMode,
    tasks,
    today,
    weekStartsOn,
    showEmptyGroups,
    t,
  ]);
};
